import { AsyncPipe } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MonitoringProductImageService } from "@domain/product/monitoring/monitoring-product-image.service";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ConfiguredProductComponent } from "@project/floorplanner/floorplan-menu/fgds/configured-product/configured-product.component";
import { FloorplanProductMenuComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-product-menu/floorplan-product-menu.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { DraggedItemType, FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { Observable, shareReplay } from "rxjs";

@Component({
  selector: "app-patient-monitor-menu",
  imports: [ConfiguredProductComponent, FloorplanProductMenuComponent, AsyncPipe],
  templateUrl: "./patient-monitor-menu.component.html",
  styleUrl: "./patient-monitor-menu.component.scss",
})
export class PatientMonitorMenuComponent {
  private floorplanService = inject(FloorplanService);
  private dragDropService = inject(FloorplanDragDropService);
  private monitoringProductImageService = inject(MonitoringProductImageService);

  protected floorplan: Floorplan;

  protected readonly tooltipTextPatientMonitors: string = $localize`:@@global.patientMonitors:Patient monitors`;
  private readonly imageUrlByConfigurationId = new Map<string, Observable<string>>();

  constructor() {
    this.floorplan = this.floorplanService.selectedFloorplan;
  }

  protected get patientMonitors(): readonly PatientMonitorConfiguration[] {
    if (this.floorplan.project instanceof MonitoringProject) {
      return this.floorplan.project.patientMonitors;
    }
    return [];
  }

  protected addPatientMonitor(patientMonitorConfig: PatientMonitorConfiguration) {
    this.floorplanService.addPatientMonitor(patientMonitorConfig);
  }

  protected addPlaceholder() {
    this.floorplanService.addPatientMonitorPlaceholder();
  }

  protected deletePatientMonitor(patientMonitorConfig: PatientMonitorConfiguration) {
    (this.floorplan.project as MonitoringProject).deletePatientMonitor(patientMonitorConfig);
  }

  protected drag(patientMonitorConfig: PatientMonitorConfiguration) {
    this.dragDropService.setDragged({ type: DraggedItemType.PATIENT_MONITOR, config: patientMonitorConfig });
  }

  protected getImageUrl$(config: PatientMonitorConfiguration): Observable<string> {
    const cached = this.imageUrlByConfigurationId.get(config.id);
    if (cached) return cached;

    const cachedImageUrl$ = this.monitoringProductImageService.resolvePatientMonitorImageUrl(config).pipe(shareReplay(1));
    this.imageUrlByConfigurationId.set(config.id, cachedImageUrl$);
    return cachedImageUrl$;
  }

  protected dragPlaceholder() {
    this.dragDropService.setDragged({ type: DraggedItemType.PATIENT_MONITOR_PLACEHOLDER });
  }

  protected updatePatientMonitorsItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanState.updatePatientMonitorsItemTextVisibility(visible);
  }
}
